// Лимиты провайдеров для сквозного блока меню (§1.2), полосы 56 и Кабинета.
//
// Источник — `GET /api/limits` (зона platform-api): Claude и Codex, окна
// 5 ч / неделя, ток/мин и прогноз. Пока эндпоинта нет (404) или он падает,
// берём Claude из старого `GET /api/usage`, а Codex показываем «нет данных».
// Один опрос раз в минуту на всю панель, сколько бы блоков ни было на экране.

import { useEffect, useState } from 'react'
import { api, getToken } from '../api'
import type { UsageResponse } from '../protocol'

export type ProviderId = 'claude' | 'codex'

export interface LimitWindow {
  kind: '5h' | 'week'
  /** израсходовано, 0–100 */
  usedPercent: number
  /** ISO-время сброса окна */
  resetsAt: string | null
}

export interface LimitForecast {
  /** хватит ли остатка окна до сброса при текущем расходе */
  enough: boolean
  /** когда кончится, если не хватит (ISO) */
  runsOutAt?: string | null
  /** расход окна 5 ч, % в час; null — оценки ещё нет */
  ratePerHour?: number | null
  /** trend — по свежим замерам, window — в среднем с начала окна, idle — расхода нет */
  basis?: string | null
}

export interface LimitProvider {
  id: ProviderId
  title: string
  available: boolean
  /** почему нет данных */
  reason?: string
  windows: LimitWindow[]
  tokensPerMin: number | null
  forecast: LimitForecast | null
}

export interface LimitsResponse {
  providers: LimitProvider[]
  /** откуда цифры: полный /api/limits или запасной /api/usage */
  source: 'limits' | 'usage'
}

const POLL_MS = 60_000

let current: LimitsResponse | null = null
let timer: number | null = null
/** /api/limits ответил 404 — дальше не спрашиваем до перезагрузки. */
let limitsMissing = false
const listeners = new Set<(value: LimitsResponse | null) => void>()

const TITLES: Record<ProviderId, string> = { claude: 'Claude', codex: 'Codex' }

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function normalize(raw: unknown): LimitProvider[] {
  const list = (raw as { providers?: unknown })?.providers
  if (!Array.isArray(list)) return []
  return list
    .map((item): LimitProvider | null => {
      const p = item as Partial<LimitProvider> & { id?: unknown }
      if (p.id !== 'claude' && p.id !== 'codex') return null
      const windows = Array.isArray(p.windows)
        ? p.windows
            .map((w) => ({
              kind: w?.kind === 'week' ? ('week' as const) : ('5h' as const),
              usedPercent: num(w?.usedPercent) ?? 0,
              resetsAt: typeof w?.resetsAt === 'string' ? w.resetsAt : null,
            }))
        : []
      const forecast = p.forecast && typeof p.forecast === 'object'
        ? {
            enough: p.forecast.enough !== false,
            runsOutAt: p.forecast.runsOutAt ?? null,
            ratePerHour: num(p.forecast.ratePerHour),
            basis: typeof p.forecast.basis === 'string' ? p.forecast.basis : null,
          }
        : null
      return {
        id: p.id,
        title: typeof p.title === 'string' && p.title ? p.title : TITLES[p.id],
        available: p.available !== false && windows.length > 0,
        reason: typeof p.reason === 'string' ? p.reason : undefined,
        windows,
        tokensPerMin: num(p.tokensPerMin),
        forecast,
      }
    })
    .filter((p): p is LimitProvider => p !== null)
}

function fromUsage(usage: UsageResponse | null, error?: string): LimitProvider[] {
  const windows: LimitWindow[] = []
  if (usage?.fiveHour) {
    windows.push({ kind: '5h', usedPercent: usage.fiveHour.percent, resetsAt: usage.fiveHour.resetsAt })
  }
  if (usage?.sevenDay) {
    windows.push({ kind: 'week', usedPercent: usage.sevenDay.percent, resetsAt: usage.sevenDay.resetsAt })
  }
  return [
    {
      id: 'claude',
      title: 'Claude',
      available: windows.length > 0,
      reason: windows.length > 0 ? undefined : usage?.error || error || 'нет данных',
      windows,
      tokensPerMin: null,
      forecast: null,
    },
    { id: 'codex', title: 'Codex', available: false, reason: 'нет данных', windows: [], tokensPerMin: null, forecast: null },
  ]
}

/** Всегда оба провайдера по порядку Claude, Codex — даже если сервер прислал одного. */
function complete(providers: LimitProvider[]): LimitProvider[] {
  return (['claude', 'codex'] as ProviderId[]).map(
    (id) =>
      providers.find((p) => p.id === id) ?? {
        id, title: TITLES[id], available: false, reason: 'нет данных', windows: [], tokensPerMin: null, forecast: null,
      },
  )
}

async function fetchLimits(): Promise<LimitProvider[] | null> {
  if (limitsMissing) return null
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['X-Agentbox-Token'] = token
  const abort = new AbortController()
  const t = window.setTimeout(() => abort.abort(), 15_000)
  try {
    const res = await fetch('/api/limits', { headers, credentials: 'same-origin', signal: abort.signal })
    if (res.status === 404 || res.status === 405) {
      limitsMissing = true
      return null
    }
    if (!res.ok) return null
    const providers = normalize(await res.json())
    return providers.length > 0 ? providers : null
  } catch {
    return null
  } finally {
    window.clearTimeout(t)
  }
}

async function poll(): Promise<void> {
  const full = await fetchLimits()
  if (full) {
    current = { providers: complete(full), source: 'limits' }
  } else {
    try {
      current = { providers: fromUsage(await api.usage()), source: 'usage' }
    } catch {
      // сеть моргнула — оставляем прошлые цифры, если были
      if (!current) current = { providers: fromUsage(null, 'нет связи'), source: 'usage' }
    }
  }
  listeners.forEach((fn) => fn(current))
}

/** Подписка на лимиты провайдеров. null — первый ответ ещё не пришёл. */
export function useLimits(): LimitsResponse | null {
  const [value, setValue] = useState(current)
  useEffect(() => {
    listeners.add(setValue)
    if (timer === null) {
      void poll()
      timer = window.setInterval(() => void poll(), POLL_MS)
    }
    return () => {
      listeners.delete(setValue)
      if (listeners.size === 0 && timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }
  }, [])
  return value
}

// ---------- производные для вида ----------

/** Остаток одного окна, 0–100. */
export function windowLeft(w: LimitWindow): number {
  return Math.max(0, Math.min(100, Math.round(100 - w.usedPercent)))
}

/** Подпись окна: «5 ч» / «неделя». */
export function windowTitle(w: LimitWindow): string {
  return w.kind === '5h' ? '5 ч' : 'неделя'
}

/** Главное окно — 5 часов (Степан, 23.09.2026: важен пятичасовой лимит и
 *  прогноз по его расходу; неделя показывается справочно). */
export function mainWindow(p: LimitProvider): LimitWindow | null {
  return p.windows.find((w) => w.kind === '5h') ?? p.windows[0] ?? null
}

/** Остаток окна 5 ч, 0–100. */
export function leftPercent(p: LimitProvider): number | null {
  const w = mainWindow(p)
  return w ? windowLeft(w) : null
}

/** Хватит ли до сброса. Без прогноза с сервера — грубо по остатку. */
export function isEnough(p: LimitProvider): boolean {
  if (p.forecast) return p.forecast.enough
  const left = leftPercent(p)
  return left === null || left >= 10
}

/** «1,9k» / «420» — ток/мин (§1.2). */
export function fmtRate(rate: number | null): string {
  if (rate === null) return '—'
  if (rate >= 1000) return (rate / 1000).toFixed(1).replace('.', ',') + 'k'
  return String(Math.round(rate))
}

/** «1 ч 57 мин» / «40 мин». */
export function fmtMins(mins: number): string {
  const m = Math.max(0, Math.round(mins))
  return m >= 60 ? `${Math.floor(m / 60)} ч ${m % 60} мин` : `${m} мин`
}

const MSK = 'Europe/Moscow'

/** «02:40» по МСК; для не сегодняшнего сброса — «пн 09:00». */
export function mskTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const hm = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: MSK })
  const day = (x: Date) => x.toLocaleDateString('ru-RU', { timeZone: MSK })
  if (day(d) === day(now)) return hm
  // завтра до полудня — всё ещё «02:40», без дня: окно 5 ч переходит через полночь
  if (d.getTime() - now.getTime() < 12 * 3600_000) return hm
  return `${d.toLocaleDateString('ru-RU', { weekday: 'short', timeZone: MSK })} ${hm}`
}

/** Текущее время МСК «23:32». */
export function mskClock(now: Date = new Date()): string {
  return now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: MSK })
}

/** «12 %/ч» — расход окна 5 ч. */
export function fmtPctRate(p: LimitProvider): string | null {
  const r = p.forecast?.ratePerHour
  if (r === null || r === undefined) return null
  return `${r < 10 ? r.toFixed(1).replace('.', ',') : Math.round(r)} %/ч`
}

/** Строка прогноза по окну 5 ч: «хватит до сброса в 16:59» или
 *  «не хватит: кончится в 15:20 (через 1 ч 10 мин), сброс в 16:59». */
export function forecastText(p: LimitProvider, now: Date = new Date()): string {
  const w = mainWindow(p)
  const reset = mskTime(w?.resetsAt)
  if (w && !w.resetsAt && w.usedPercent <= 0) return 'окно 5 ч не начато'
  if (isEnough(p)) {
    if (p.forecast?.basis === 'unknown') return `до сброса в ${reset} — расход ещё считается`
    return `хватит до сброса в ${reset}`
  }
  const out = p.forecast?.runsOutAt ? new Date(p.forecast.runsOutAt) : null
  if (out && !Number.isNaN(out.getTime())) {
    const mins = (out.getTime() - now.getTime()) / 60_000
    return `не хватит: кончится в ${mskTime(p.forecast!.runsOutAt)} (через ${fmtMins(mins)}), сброс в ${reset}`
  }
  return `не хватит до сброса в ${reset}`
}
